// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { ExternalLink, Play, MapPin, Calendar, Users, Trophy, Star } from 'lucide-react';
import { SocialMediaIcons } from '@/components/profile/SocialMediaIcons';
import heroImage from '@/assets/recruitlook-hoops-circuit-hero.jpg';
import jucoShowcasePromo from '@/assets/juco-showcase-promo.jpg';
import aajucoLogo from '@/assets/aajuco-showcase-logo.png';
interface FeaturedPlayer {
  id: string;
  name: string;
  position: string | null;
  class_year: string | null;
  height: string | null;
  photo_url: string | null;
  video_url: string | null;
  twitter_url: string | null;
  instagram_url: string | null;
  evaluation_notes: string | null;
  display_order: number;
}

interface JucoAttendee {
  id: string;
  name: string;
  school: string | null;
  position: string | null;
  player_category: string | null;
  is_confirmed: boolean;
}

interface CoachStats {
  total_coaches: number;
  d1_coaches: number;
  d2_coaches: number;
  d3_coaches: number;
  naia_coaches: number;
  juco_coaches: number;
  testimonial_text: string | null;
  testimonial_author: string | null;
}

interface ConfirmedCoach {
  id: string;
  coach_name: string;
  college_name: string;
  division: string | null;
  title: string | null;
}

const MayLivePeriodKC: React.FC = () => {
  const [players, setPlayers] = useState<FeaturedPlayer[]>([]);
  const [jucoAttendees, setJucoAttendees] = useState<JucoAttendee[]>([]);
  const [confirmedCoaches, setConfirmedCoaches] = useState<ConfirmedCoach[]>([]);
  const [coachStats, setCoachStats] = useState<CoachStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured players
        const { data: playersData } = await supabase
          .from('featured_may_players')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        // Fetch JUCO attendees
        const { data: jucoData } = await supabase
          .from('juco_showcase_attendees')
          .select('*')
          .eq('is_confirmed', true)
          .order('display_order', { ascending: true });

        // Fetch confirmed college coaches
        const { data: coachesData } = await supabase
          .from('may_event_confirmed_coaches')
          .select('*')
          .eq('is_confirmed', true)
          .order('display_order', { ascending: true });

        // Fetch coach stats
        const { data: statsData } = await supabase
          .from('event_coach_stats')
          .select('*')
          .eq('event_identifier', 'may_live_kc_2025')
          .single();

        if (playersData) setPlayers(playersData);
        if (jucoData) setJucoAttendees(jucoData);
        if (coachesData) setConfirmedCoaches(coachesData);
        if (statsData) setCoachStats(statsData);
      } catch (error) {
        console.error('Error fetching event data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const playerCategories = [
    { key: 'returning_sophomore', label: 'Returning JUCO Sophomores (2027 JUCO Class)' },
    { key: 'unsigned', label: 'Available/Unsigned JUCO and High School/Prep Prospects (2026 Class)' },
    { key: 'post_grad', label: 'Post Grads & Preps' },
    { key: 'transfer', label: 'Transfers' },
    { key: 'international', label: 'International Prospects' },
  ];

  return (
    <>
      <Helmet>
        <title>May Live Period - Kansas City | RecruitLook</title>
        <meta name="description" content="Join 500+ college coaches at the May Live Period in Kansas City. Elite basketball talent, top exposure, premium coach packets available." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* HERO SECTION */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroImage})`,
              backgroundPosition: 'center top'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
          </div>
          
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <Badge className="mb-4 bg-secondary text-secondary-foreground text-sm px-4 py-1">
              LIVE PERIOD EVENT
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-display">
              May Live Period
            </h1>
            <h2 className="text-2xl md:text-3xl text-white/90 mb-6">
              Kansas City
            </h2>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-white/90 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-secondary" />
                <span className="text-lg">May 16-18, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-secondary" />
                <span className="text-lg">Kansas City, MO</span>
              </div>
            </div>

            <Button 
              size="lg" 
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold text-lg px-8"
              onClick={() => document.getElementById('coach-packet')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get Your Coach Packet
            </Button>
          </div>
        </section>

        {/* COACH ATTENDANCE STATS SECTION */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
                College Coach Attendance
              </h2>
              <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto">
                RecruitLook events consistently attract the nation's top college basketball programs
              </p>
            </div>

            {coachStats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-4xl md:text-5xl font-bold text-secondary mb-2">
                      {coachStats.total_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">Total Coaches</div>
                  </div>
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {coachStats.d1_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">Division I</div>
                  </div>
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {coachStats.d2_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">Division II</div>
                  </div>
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {coachStats.d3_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">Division III</div>
                  </div>
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {coachStats.naia_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">NAIA</div>
                  </div>
                  <div className="text-center p-6 bg-white/10 rounded-lg">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {coachStats.juco_coaches}+
                    </div>
                    <div className="text-sm text-primary-foreground/80">JUCO</div>
                  </div>
                </div>

                {coachStats.testimonial_text && (
                  <div className="max-w-3xl mx-auto text-center">
                    <blockquote className="text-xl italic text-primary-foreground/90 mb-4">
                      "{coachStats.testimonial_text}"
                    </blockquote>
                    {coachStats.testimonial_author && (
                      <cite className="text-secondary font-semibold">
                        — {coachStats.testimonial_author}
                      </cite>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* FEATURED PLAYERS SECTION */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-primary text-primary-foreground">
                <Star className="h-4 w-4 mr-1" />
                FEATURED TALENT
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
                Featured Players
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Top prospects competing at the May Live Period Kansas City
              </p>
            </div>

            {players.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {players.map((player) => (
                  <Card key={player.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative aspect-[3/4] bg-muted">
                      {player.photo_url ? (
                        <img
                          src={player.photo_url}
                          alt={player.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/40">
                          <Users className="h-16 w-16 text-primary/50" />
                        </div>
                      )}
                      
                      {player.video_url && (
                        <a
                          href={player.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute top-3 right-3 bg-destructive hover:bg-destructive/90 text-destructive-foreground p-2 rounded-full transition-colors"
                        >
                          <Play className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg text-foreground mb-1">{player.name}</h3>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {player.position && (
                          <Badge variant="secondary" className="text-xs">
                            {player.position}
                          </Badge>
                        )}
                        {player.class_year && (
                          <Badge variant="outline" className="text-xs">
                            {player.class_year}
                          </Badge>
                        )}
                        {player.height && (
                          <Badge variant="outline" className="text-xs">
                            {player.height}
                          </Badge>
                        )}
                      </div>

                      {player.evaluation_notes && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                          {player.evaluation_notes}
                        </p>
                      )}

                      <div className="flex items-center gap-3">
                        {player.twitter_url && (
                          <a
                            href={player.twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <SocialMediaIcons platform="twitter" className="h-5 w-5" />
                          </a>
                        )}
                        {player.instagram_url && (
                          <a
                            href={player.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <SocialMediaIcons platform="instagram" className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Trophy className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">
                  Featured players coming soon!
                </p>
              </div>
            )}
          </div>
        </section>

        {/* COACH PACKET PURCHASE SECTION */}
        <section id="coach-packet" className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-secondary text-secondary-foreground">
                  FOR COLLEGE COACHES
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
                  Coach Packet
                </h2>
                <p className="text-muted-foreground text-lg">
                  Get comprehensive access to all event information and player profiles
                </p>
              </div>

              <Card className="border-2 border-secondary/30">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-6">What's Included</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary text-sm">✓</span>
                          </div>
                          <span className="text-muted-foreground">Complete team rosters with player profiles</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary text-sm">✓</span>
                          </div>
                          <span className="text-muted-foreground">Game schedules and court assignments</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary text-sm">✓</span>
                          </div>
                          <span className="text-muted-foreground">Player stats and evaluation notes</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary text-sm">✓</span>
                          </div>
                          <span className="text-muted-foreground">QR codes linked to player video highlights</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="h-6 w-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-secondary text-sm">✓</span>
                          </div>
                          <span className="text-muted-foreground">Contact information for coaches and families</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="flex flex-col justify-center items-center text-center bg-muted/50 rounded-lg p-8">
                      <div className="text-4xl font-bold text-foreground mb-2">$49</div>
                      <p className="text-muted-foreground mb-6">One-time purchase</p>
                      <Button 
                        size="lg" 
                        className="bg-primary hover:bg-primary/90 text-primary-foreground w-full max-w-xs"
                      >
                        Purchase Coach Packet
                      </Button>
                      <p className="text-sm text-muted-foreground mt-4">
                        Digital delivery • Instant access
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  Questions about coach packets or event registration?
                </p>
                <Button variant="outline" asChild>
                  <a href="mailto:coaches@recruitlook.com">
                    Contact Us
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ALL-AMERICAN JUCO SHOWCASE PARTNERSHIP SECTION */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-primary text-primary-foreground">
                PARTNERSHIP
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
                All-American JUCO Showcase
              </h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                All games at the same facility as RecruitLook 17u Teams
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
              {/* JUCO Event Graphic */}
              <Card className="overflow-hidden shadow-lg">
                <img 
                  src={jucoShowcasePromo} 
                  alt="RecruitLook Hoops x All-American JUCO Showcase - May 15-17 NCAA Certified Event"
                  className="w-full h-auto object-cover"
                />
              </Card>

              {/* All American JUCO Logo */}
              <Card className="overflow-hidden shadow-lg bg-white flex items-center justify-center p-8">
                <img 
                  src={aajucoLogo} 
                  alt="All-American JUCO Showcase Logo"
                  className="w-full max-w-sm h-auto object-contain"
                />
              </Card>
            </div>

            {/* Player Categories */}
            <div className="max-w-3xl mx-auto mb-12">
              <h3 className="text-xl font-bold text-foreground mb-6 text-center">Player Categories</h3>
              <div className="grid gap-3">
                {playerCategories.map((category) => (
                  <div 
                    key={category.key}
                    className="flex items-center gap-3 p-4 bg-background rounded-lg border"
                  >
                    <div className="h-3 w-3 rounded-full bg-secondary" />
                    <span className="text-foreground">{category.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Confirmed Attendees */}
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-foreground mb-6 text-center">
                Confirmed Attendees
              </h3>
              
              {jucoAttendees.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {jucoAttendees.map((attendee) => (
                    <Card key={attendee.id} className="p-4">
                      <h4 className="font-semibold text-foreground">{attendee.name}</h4>
                      {attendee.school && (
                        <p className="text-sm text-muted-foreground">{attendee.school}</p>
                      )}
                      {attendee.position && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {attendee.position}
                        </Badge>
                      )}
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg font-medium">Coming Soon</p>
                  <p className="text-sm text-muted-foreground/60 mt-2">
                    Confirmed attendees will be listed here as they register
                  </p>
                </Card>
              )}
            </div>
          </div>
        </section>

        {/* CONFIRMED COLLEGE COACHES SECTION */}
        {confirmedCoaches.length > 0 && (
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-primary text-primary-foreground">
                  <Users className="h-4 w-4 mr-1" />
                  CONFIRMED ATTENDEES
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
                  College Coaches Attending
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  These college coaches have confirmed their attendance
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 max-w-6xl mx-auto">
                {confirmedCoaches.map((coach) => (
                  <Card key={coach.id} className="p-4 text-center hover:shadow-lg transition-shadow">
                    <h4 className="font-semibold text-foreground text-sm">{coach.coach_name}</h4>
                    <p className="text-primary font-medium text-sm mt-1">{coach.college_name}</p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      {coach.division && (
                        <Badge variant="secondary" className="text-xs">{coach.division}</Badge>
                      )}
                      {coach.title && (
                        <Badge variant="outline" className="text-xs">{coach.title}</Badge>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FOOTER CONTACT SECTION */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold mb-4">Questions?</h3>
            <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
              Contact our team for more information about the May Live Period Kansas City
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button 
                variant="secondary" 
                asChild
              >
                <a href="mailto:coaches@recruitlook.com">
                  Email: coaches@recruitlook.com
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MayLivePeriodKC;
